// Frontend i18n wrapper.
// Base strings live in i18nBase.js; approved UI-copy changes can be layered
// here without disturbing the broader translation catalogue during review.
import { makeT as makeBaseT, normalizeLang } from "./i18nBase";

export { normalizeLang } from "./i18nBase";

export const OVERRIDES = {
  en: {
    "rp.repracticeMeta": "One re-practice available · Uses 1 interview session",
    "rp.repracticeConfirmIntro": "You can re-practice this interview once using the exact same questions. This will use 1 interview session.",
    "rp.repracticeErrNoCredits": "You’ve used all the interview sessions in your 30-day access. Your reports are still available.",
    "sessions.today": (used, limit = 5) => `Interview sessions today: ${used} of ${limit} used`,
    "sessions.limitReached": (limit = 5) => `You’ve used all ${limit} interview sessions available today. Your session allowance resets at midnight.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Interview sessions today: ${used} of ${limit} used · Resets at midnight`,
    "sen.eyebrow": "Interview Level Detected",
    "iv.tapMic": "Click/Tap to Speak",
    "iv.restartInterview": "Restart",
    "iv.submitting": "Scoring…",
    "iv.clarify": "I didn’t understand the question",
    "iv.clarifying": "Rephrasing question…",
    "iv.clarifyUsed": "Question rephrased",
    "iv.clarifyFail": "We couldn't rephrase this question. Please continue with the original question.",
    "iv.simplifiedQuestion": "Simplified question",
    "iv.warmupEye": "Warm-up Question",
    "iv.warmupTitle": "Let's start with a quick warm-up",
    "iv.warmupIntro": "Before we begin the interview, we'll do a brief warm-up question. Its purpose is to help you test your microphone and speakers and experience how the rest of interview session works. Your response is not evaluated and does not affect your score or reports.",
    "iv.warmupBanner": "This warm-up is just for you to test the audio to ensure you can hear and respond to a question. It is not used in scoring your interview.",
    "rp.clarificationH": "Question clarification requests",
    "rp.clarificationBody": (used, total) => `${used} of ${total} interview questions were rephrased in simpler English.`,
    "rp.clarificationNone": "You did not request a simpler version of any interview question.",
    "rp.clarificationNote": "Clarification requests are reported as a comprehension signal and do not automatically reduce your interview score.",
    // Follow-ups (FOLLOW_UP_PLAN.md §9)
    "iv.checking": "One moment…",
    "iv.followUpLabel": "Follow-up question",
    "iv.followUpHint": "Answer it the same way: record, then submit.",
    "iv.followUpHidden": "Listen to the follow-up question.",
    "iv.repeatH": "Please answer again",
    "iv.repeatP": "The interviewer couldn’t understand your answer. Record it again.",
    "rp.followUp": "Follow-up question",
    "rp.followUpAnswer": "Your answer to the follow-up",
    "rp.tip": "Tip",
    "rp.exampleTip": "When you don’t have a direct example, use a similar one from school, volunteering or another job.",
    "rp.exampleTipSenior": "When you don’t have a direct example, use a similar one from a previous role, a project or volunteering.",
    // Step 5 report (SCORING_PLAN.md §5, 2026-10-28).
    "rp.bandDeveloping": "Developing",
    "rp.bandFunctional": "Functional",
    "rp.bandStrong": "Strong",
    "rp.ipShort": "Interview",
    "rp.englishShort": "English",
    "rp.completeFirst": (n, m) => `Complete on the first answer: ${n} of ${m} questions`,
    "rp.completeFirstHint": "Questions you answered fully without a follow-up question. It is not part of any score.",
    "rp.firstTipPoint": "Next time, include this in your first answer, without waiting to be asked.",
    "rp.firstTipResult": "Next time, finish your example by saying how it turned out.",
    "rp.firstTipExample": "Next time, start with one specific example from your own experience.",
    "rp.firstTipRoleLink": "Next time, connect your answer to this position.",
    "rp.resultsNotScored": "Evidence of results not scored: the question didn't ask for a result.",
    "rp.englishFeedbackH": "English feedback",
    "rp.englishPatternsH": "Patterns to work on",
    "rp.englishSaid": "You said",
    "rp.englishBetter": "Better",
    "rp.englishStrengthH": "What your English already does well",
    "rp.compareH": "Compared with your first attempt",
    "rp.compareFirst": "First attempt",
    "rp.compareNow": "This attempt",
    "rp.compareCompleteFirst": "Complete on the first answer",
  },
  "pt-BR": {
    "rp.repracticeMeta": "Uma nova prática disponível · Usa 1 sessão de entrevista",
    "rp.repracticeConfirmIntro": "Você pode praticar esta entrevista novamente uma vez usando exatamente as mesmas perguntas. Isto usará 1 sessão de entrevista.",
    "rp.repracticeErrNoCredits": "Você já usou todas as sessões de entrevista do seu acesso de 30 dias. Seus relatórios continuam disponíveis.",
    "sessions.today": (used, limit = 5) => `Sessões de entrevista hoje: ${used} de ${limit} usadas`,
    "sessions.limitReached": (limit = 5) => `Você já usou as ${limit} sessões de entrevista disponíveis hoje. Seu limite de sessões é renovado à meia-noite.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Sessões de entrevista hoje: ${used} de ${limit} usadas · Renova à meia-noite`,
    "sen.eyebrow": "Nível da entrevista detectado",
    "iv.tapMic": "Clique/toque para falar",
    "iv.restartInterview": "Reiniciar",
    "iv.submitting": "Avaliando…",
    "iv.clarify": "Não entendi a pergunta",
    "iv.clarifying": "Reformulando a pergunta…",
    "iv.clarifyUsed": "Pergunta reformulada",
    "iv.clarifyFail": "Não foi possível reformular esta pergunta. Continue com a pergunta original.",
    "iv.simplifiedQuestion": "Pergunta simplificada",
    "iv.warmupEye": "Pergunta de aquecimento",
    "iv.warmupTitle": "Vamos começar com um aquecimento rápido",
    "iv.warmupIntro": "Antes de começarmos a entrevista, faremos uma breve pergunta de aquecimento. O objetivo é ajudar você a testar o microfone e os alto-falantes e entender como o restante da sessão de entrevista funciona. Sua resposta não é avaliada e não afeta sua pontuação nem seus relatórios.",
    "iv.warmupBanner": "Este aquecimento serve apenas para testar o áudio e confirmar que você consegue ouvir e responder a uma pergunta. Ele não é usado na pontuação da sua entrevista.",
    "rp.clarificationH": "Pedidos de esclarecimento das perguntas",
    "rp.clarificationBody": (used, total) => `${used} de ${total} perguntas da entrevista foram reformuladas em inglês mais simples.`,
    "rp.clarificationNone": "Você não pediu uma versão mais simples de nenhuma pergunta da entrevista.",
    "rp.clarificationNote": "Os pedidos de esclarecimento são apresentados como um sinal de compreensão e não reduzem automaticamente sua pontuação na entrevista.",
    // Follow-ups (FOLLOW_UP_PLAN.md §9)
    "iv.checking": "Um momento…",
    "iv.followUpLabel": "Pergunta de acompanhamento",
    "iv.followUpHint": "Responda da mesma forma: grave e depois envie.",
    "iv.followUpHidden": "Ouça a pergunta de acompanhamento.",
    "iv.repeatH": "Responda novamente, por favor",
    "iv.repeatP": "O entrevistador não conseguiu entender sua resposta. Grave novamente.",
    "rp.followUp": "Pergunta de acompanhamento",
    "rp.followUpAnswer": "Sua resposta à pergunta de acompanhamento",
    "rp.tip": "Dica",
    "rp.exampleTip": "Quando você não tiver um exemplo direto, use um parecido da escola, de trabalho voluntário ou de outro emprego.",
    "rp.exampleTipSenior": "Quando você não tiver um exemplo direto, use um parecido de um cargo anterior, de um projeto ou de trabalho voluntário.",
    // Step 5 report (SCORING_PLAN.md §5, 2026-10-28).
    "rp.bandDeveloping": "Em desenvolvimento",
    "rp.bandFunctional": "Funcional",
    "rp.bandStrong": "Forte",
    "rp.ipShort": "Entrevista",
    "rp.englishShort": "Inglês",
    "rp.completeFirst": (n, m) => `Completas na primeira resposta: ${n} de ${m} perguntas`,
    "rp.completeFirstHint": "Perguntas que você respondeu por completo, sem pergunta de acompanhamento. Não faz parte de nenhuma nota.",
    "rp.firstTipPoint": "Da próxima vez, inclua isso já na primeira resposta, sem esperar que perguntem.",
    "rp.firstTipResult": "Da próxima vez, termine seu exemplo dizendo qual foi o resultado.",
    "rp.firstTipExample": "Da próxima vez, comece com um exemplo específico da sua própria experiência.",
    "rp.firstTipRoleLink": "Da próxima vez, relacione sua resposta a esta vaga.",
    "rp.resultsNotScored": "Evidência de resultados não avaliada: a pergunta não pedia um resultado.",
    "rp.englishFeedbackH": "Feedback sobre o seu inglês",
    "rp.englishPatternsH": "Padrões para trabalhar",
    "rp.englishSaid": "Você disse",
    "rp.englishBetter": "Melhor",
    "rp.englishStrengthH": "O que o seu inglês já faz bem",
    "rp.compareH": "Comparado com a sua primeira tentativa",
    "rp.compareFirst": "Primeira tentativa",
    "rp.compareNow": "Esta tentativa",
    "rp.compareCompleteFirst": "Completas na primeira resposta",
  },
  "es-419": {
    "rp.repracticeMeta": "Una nueva práctica disponible · Usa 1 sesión de entrevista",
    "rp.repracticeConfirmIntro": "Puede practicar esta entrevista una vez más utilizando exactamente las mismas preguntas. Esto utilizará 1 sesión de entrevista.",
    "rp.repracticeErrNoCredits": "Ya utilizó todas las sesiones de entrevista de su acceso de 30 días. Sus informes siguen disponibles.",
    "sessions.today": (used, limit = 5) => `Sesiones de entrevista hoy: ${used} de ${limit} utilizadas`,
    "sessions.limitReached": (limit = 5) => `Ya utilizó las ${limit} sesiones de entrevista disponibles hoy. Su límite de sesiones se restablece a medianoche.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Sesiones de entrevista hoy: ${used} de ${limit} utilizadas · Se restablece a medianoche`,
    "sen.eyebrow": "Nivel de entrevista detectado",
    "iv.tapMic": "Haz clic/toca para hablar",
    "iv.restartInterview": "Reiniciar",
    "iv.submitting": "Evaluando…",
    "iv.clarify": "No entendí la pregunta",
    "iv.clarifying": "Reformulando pregunta…",
    "iv.clarifyUsed": "Pregunta reformulada",
    "iv.clarifyFail": "No pudimos reformular esta pregunta. Continúe con la pregunta original.",
    "iv.simplifiedQuestion": "Pregunta simplificada",
    "iv.warmupEye": "Pregunta de calentamiento",
    "iv.warmupTitle": "Empecemos con un calentamiento rápido",
    "iv.warmupIntro": "Antes de comenzar la entrevista, haremos una breve pregunta de calentamiento. Su propósito es ayudarle a probar el micrófono y los altavoces y a familiarizarse con el funcionamiento del resto de la sesión de entrevista. Su respuesta no se evalúa y no afecta su puntuación ni sus informes.",
    "iv.warmupBanner": "Este calentamiento sirve solo para probar el audio y confirmar que puede oír y responder una pregunta. No se usa para puntuar su entrevista.",
    "rp.clarificationH": "Solicitudes de aclaración de preguntas",
    "rp.clarificationBody": (used, total) => `${used} de ${total} preguntas de la entrevista se reformularon en un inglés más sencillo.`,
    "rp.clarificationNone": "No solicitó una versión más sencilla de ninguna pregunta de la entrevista.",
    "rp.clarificationNote": "Las solicitudes de aclaración se presentan como una señal de comprensión y no reducen automáticamente su puntuación de la entrevista.",
    // Follow-ups (FOLLOW_UP_PLAN.md §9)
    "iv.checking": "Un momento…",
    "iv.followUpLabel": "Pregunta de seguimiento",
    "iv.followUpHint": "Respóndala de la misma manera: grabe y luego envíe.",
    "iv.followUpHidden": "Escuche la pregunta de seguimiento.",
    "iv.repeatH": "Por favor, responda de nuevo",
    "iv.repeatP": "El entrevistador no pudo entender su respuesta. Grábela de nuevo.",
    "rp.followUp": "Pregunta de seguimiento",
    "rp.followUpAnswer": "Su respuesta a la pregunta de seguimiento",
    "rp.tip": "Consejo",
    "rp.exampleTip": "Cuando no tenga un ejemplo directo, use uno similar de la escuela, del voluntariado o de otro trabajo.",
    "rp.exampleTipSenior": "Cuando no tenga un ejemplo directo, use uno similar de un puesto anterior, de un proyecto o del voluntariado.",
    // Step 5 report (SCORING_PLAN.md §5, 2026-10-28).
    "rp.bandDeveloping": "En desarrollo",
    "rp.bandFunctional": "Funcional",
    "rp.bandStrong": "Sólido",
    "rp.ipShort": "Entrevista",
    "rp.englishShort": "Inglés",
    "rp.completeFirst": (n, m) => `Completas en la primera respuesta: ${n} de ${m} preguntas`,
    "rp.completeFirstHint": "Preguntas que respondió por completo, sin pregunta de seguimiento. No forma parte de ninguna puntuación.",
    "rp.firstTipPoint": "La próxima vez, inclúyalo en su primera respuesta, sin esperar a que se lo pregunten.",
    "rp.firstTipResult": "La próxima vez, termine su ejemplo diciendo cómo resultó.",
    "rp.firstTipExample": "La próxima vez, empiece con un ejemplo específico de su propia experiencia.",
    "rp.firstTipRoleLink": "La próxima vez, relacione su respuesta con este puesto.",
    "rp.resultsNotScored": "Evidencia de resultados sin puntuar: la pregunta no pedía un resultado.",
    "rp.englishFeedbackH": "Comentarios sobre su inglés",
    "rp.englishPatternsH": "Patrones para trabajar",
    "rp.englishSaid": "Usted dijo",
    "rp.englishBetter": "Mejor",
    "rp.englishStrengthH": "Lo que su inglés ya hace bien",
    "rp.compareH": "Comparado con su primer intento",
    "rp.compareFirst": "Primer intento",
    "rp.compareNow": "Este intento",
    "rp.compareCompleteFirst": "Completas en la primera respuesta",
  },
  "fr-CA": {
    "rp.repracticeMeta": "Une nouvelle pratique disponible · Utilise 1 session d’entrevue",
    "rp.repracticeConfirmIntro": "Vous pouvez refaire cette entrevue une seule fois avec exactement les mêmes questions. Cela utilisera 1 session d’entrevue.",
    "rp.repracticeErrNoCredits": "Vous avez utilisé toutes les sessions d’entrevue de votre accès de 30 jours. Vos rapports restent disponibles.",
    "sessions.today": (used, limit = 5) => `Sessions d’entrevue aujourd’hui : ${used} sur ${limit} utilisées`,
    "sessions.limitReached": (limit = 5) => `Vous avez utilisé les ${limit} sessions d’entrevue disponibles aujourd’hui. Votre allocation de sessions est réinitialisée à minuit.`,
    "sessions.limitTracker": (used = 5, limit = 5) => `Sessions d’entrevue aujourd’hui : ${used} sur ${limit} utilisées · Réinitialisation à minuit`,
    "sen.eyebrow": "Niveau d’entrevue détecté",
    "iv.tapMic": "Cliquez/touchez pour parler",
    "iv.restartInterview": "Recommencer",
    "iv.submitting": "Évaluation…",
    "iv.clarify": "Je n’ai pas compris la question",
    "iv.clarifying": "Reformulation de la question…",
    "iv.clarifyUsed": "Question reformulée",
    "iv.clarifyFail": "Nous n’avons pas pu reformuler cette question. Veuillez continuer avec la question originale.",
    "iv.simplifiedQuestion": "Question simplifiée",
    "iv.warmupEye": "Question d’échauffement",
    "iv.warmupTitle": "Commençons par un bref échauffement",
    "iv.warmupIntro": "Avant de commencer l’entrevue, nous ferons une brève question d’échauffement. Elle sert à vous aider à tester votre microphone et vos haut-parleurs et à vous familiariser avec le fonctionnement du reste de la séance d’entrevue. Votre réponse n’est pas évaluée et n’a aucun effet sur votre résultat ni vos rapports.",
    "iv.warmupBanner": "Cet échauffement sert uniquement à tester l’audio et à vérifier que vous pouvez entendre une question et y répondre. Il n’est pas utilisé pour noter votre entrevue.",
    "rp.clarificationH": "Demandes de clarification des questions",
    "rp.clarificationBody": (used, total) => `${used} des ${total} questions d’entrevue ont été reformulées en anglais plus simple.`,
    "rp.clarificationNone": "Vous n’avez demandé une version simplifiée d’aucune question d’entrevue.",
    "rp.clarificationNote": "Les demandes de clarification sont présentées comme un indicateur de compréhension et ne réduisent pas automatiquement votre résultat d’entrevue.",
    // Follow-ups (FOLLOW_UP_PLAN.md §9)
    "iv.checking": "Un instant…",
    "iv.followUpLabel": "Question de suivi",
    "iv.followUpHint": "Répondez de la même façon : enregistrez, puis envoyez.",
    "iv.followUpHidden": "Écoutez la question de suivi.",
    "iv.repeatH": "Veuillez répondre de nouveau",
    "iv.repeatP": "L’intervieweur n’a pas compris votre réponse. Enregistrez-la de nouveau.",
    "rp.followUp": "Question de suivi",
    "rp.followUpAnswer": "Votre réponse à la question de suivi",
    "rp.tip": "Conseil",
    "rp.exampleTip": "Si vous n’avez pas d’exemple direct, utilisez-en un semblable tiré de vos études, du bénévolat ou d’un autre emploi.",
    "rp.exampleTipSenior": "Si vous n’avez pas d’exemple direct, utilisez-en un semblable tiré d’un poste précédent, d’un projet ou du bénévolat.",
    // Step 5 report (SCORING_PLAN.md §5, 2026-10-28).
    "rp.bandDeveloping": "En développement",
    "rp.bandFunctional": "Fonctionnel",
    "rp.bandStrong": "Solide",
    "rp.ipShort": "Entrevue",
    "rp.englishShort": "Anglais",
    "rp.completeFirst": (n, m) => `Réponses complètes du premier coup : ${n} sur ${m} questions`,
    "rp.completeFirstHint": "Les questions auxquelles vous avez répondu complètement, sans question de suivi. Cela ne compte dans aucune note.",
    "rp.firstTipPoint": "La prochaine fois, incluez cet élément dès votre première réponse, sans attendre qu’on vous le demande.",
    "rp.firstTipResult": "La prochaine fois, terminez votre exemple en disant comment cela s’est terminé.",
    "rp.firstTipExample": "La prochaine fois, commencez par un exemple précis tiré de votre propre expérience.",
    "rp.firstTipRoleLink": "La prochaine fois, reliez votre réponse à ce poste.",
    "rp.resultsNotScored": "Preuves de résultats non évaluées : la question ne demandait pas de résultat.",
    "rp.englishFeedbackH": "Rétroaction sur votre anglais",
    "rp.englishPatternsH": "Points à travailler",
    "rp.englishSaid": "Vous avez dit",
    "rp.englishBetter": "Mieux",
    "rp.englishStrengthH": "Ce que votre anglais fait déjà bien",
    "rp.compareH": "Comparaison avec votre première tentative",
    "rp.compareFirst": "Première tentative",
    "rp.compareNow": "Cette tentative",
    "rp.compareCompleteFirst": "Réponses complètes du premier coup",
  },
};

export function makeT(lang) {
  const code = normalizeLang(lang);
  const baseT = makeBaseT(lang);
  const table = OVERRIDES[code] || OVERRIDES.en;
  return (key, ...args) => {
    const value = table[key];
    if (typeof value === "function") return value(...args);
    if (value !== undefined) return value;
    return baseT(key, ...args);
  };
}
